import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  FlatList,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { callClaimFoodOrder } from '../services/cloudFunctions';
import { locationService } from '../services/locationService';
import { biometricService } from '../services/biometricService';
import { haversineKm } from '../services/geoUtils';
import { volver } from '../utils/volver';
import { mensajeDeError } from '../utils/mensajeDeError';
import { pesos } from '../utils/dinero';

/**
 * Pantalla del repartidor (DELIVERY_DRIVER) para ver pedidos READY y
 * reclamarlos atómicamente. La transacción server-side garantiza que
 * solo el primero gana — no race condition.
 *
 * Ordena por distancia ascendente desde la posición del driver.
 */
export default function DeliveriesFeedScreen() {
  const router = useRouter();
  const userRole = useAppStore((s) => s.userRole);
  const miUid = useAppStore((s) => s.userFirebaseUid);
  // LA ENTREGA QUE YA TOMÓ Y NO HA TERMINADO.
  // La única entrada a `/delivery-active` era el salto de justo después de
  // reclamar el pedido: cerrar la app dejaba al repartidor con la comida encima
  // y sin camino de vuelta — el pedido ya no está READY, así que tampoco sale
  // en esta lista. Este aviso es esa puerta.
  const [entregaEnCurso, setEntregaEnCurso] = useState<any | null>(null);

  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  // POR QUÉ UN ERROR EN PANTALLA Y NO SOLO `Alert.alert`: en web (react-native-web)
  // `Alert` no dibuja nada —es un no-op—, así que un fallo contado solo por ahí
  // vuelve a ser un botón que "no hace nada". Este texto se ve en las dos
  // plataformas y se limpia en cuanto el repartidor vuelve a intentarlo.
  const [errorTomar, setErrorTomar] = useState<string | null>(null);
  const [myLat, setMyLat] = useState<number | null>(null);
  const [myLng, setMyLng] = useState<number | null>(null);

  // El comentario de aquí decía «subir a tracking ACTIVE» y el código pone IDLE.
  // Manda el código, y hace bien: mirando la lista todavía no hay entrega que
  // nadie siga en el mapa, así que el GPS va en el modo de ahorro (solo hay dos,
  // IDLE y ACTIVE_RIDE). Sube a ACTIVE_RIDE al tomar el pedido, en handleClaim.
  useEffect(() => {
    locationService.setTrackingMode('IDLE').catch(() => {});
    // Toma una lectura puntual para ordenar por distancia
    locationService.getCurrentLocation().then((pos: any) => {
      if (pos?.coords) {
        setMyLat(pos.coords.latitude);
        setMyLng(pos.coords.longitude);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const unsub = firestoreService.listenToAvailableDeliveries((items: any[]) => {
      setOrders(items);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!miUid) return;
    const unsub = firestoreService.listenToMiEntregaActiva(miUid, setEntregaEnCurso);
    return () => unsub();
  }, [miUid]);

  const sorted = useMemo(() => {
    return orders
      .map((o) => {
        let distanceKm: number | null = null;
        if (myLat !== null && myLng !== null && typeof o.pickupLat === 'number' && typeof o.pickupLng === 'number') {
          distanceKm = haversineKm(
            { lat: myLat, lng: myLng },
            { lat: o.pickupLat, lng: o.pickupLng }
          );
        }
        return { ...o, distanceKm };
      })
      .sort((a, b) => {
        if (a.distanceKm !== null && b.distanceKm !== null) return a.distanceKm - b.distanceKm;
        if (a.distanceKm !== null) return -1;
        if (b.distanceKm !== null) return 1;
        const tA = a.createdAt?.toMillis?.() ?? 0;
        const tB = b.createdAt?.toMillis?.() ?? 0;
        return tB - tA;
      });
  }, [orders, myLat, myLng]);

  const handleClaim = async (orderId: string) => {
    if (claimingId) return;
    setErrorTomar(null);

    // GUARDIÁN BIOMÉTRICO — viene del panel del mapa, que era desde donde se
    // aceptaba antes. No se pierde al mudar el botón aquí: el que toma un pedido
    // (con la dirección y el teléfono de un cliente dentro) tiene que ser el
    // dueño del teléfono. `authenticateUser` ya devuelve `true` cuando el aparato
    // NO PUEDE verificar —sin lector, sin PIN, o web—, así que esto no encierra a
    // nadie en un emulador limpio (que es con lo que revisa Google Play).
    const esElDueno = await biometricService.authenticateUser(
      'Verifica tu identidad para tomar este pedido'
    );
    if (!esElDueno) {
      setErrorTomar('No pudimos verificar tu identidad. Inténtalo otra vez.');
      Alert.alert('Acceso denegado', 'No pudimos verificar tu identidad.');
      return;
    }

    setClaimingId(orderId);
    try {
      await callClaimFoodOrder({ orderId });

      // El pedido ya es suyo: sube a alta precisión para que el restaurante y el
      // cliente vean su movimiento en vivo. Iba en el panel del mapa y se muda
      // con el botón; sin esto el GPS se quedaba en IDLE (lo baja el efecto de
      // arriba) durante toda la entrega. Si no estaba conectado no hay vigilante
      // que subir: lo enciende delivery-active al abrirse (V17).
      locationService.setTrackingMode('ACTIVE_RIDE').catch(() => {});

      // NAVEGAR DIRECTO, NO DESDE EL BOTÓN DE UN `Alert`. El aviso de éxito tenía
      // el `router.push` colgado del `onPress` del OK, y como `Alert` no existe
      // en web el pedido quedaba tomado pero la pantalla de entrega no se abría
      // nunca: el repartidor se quedaba mirando la lista con el pedido ya suyo.
      router.push(`/delivery-active?orderId=${orderId}` as any);
    } catch (e: any) {
      // NUNCA EN SILENCIO. `claimFoodOrder` distingue los casos y su mensaje ya
      // viene en español ("ya fue tomado por otro repartidor", "completa tu
      // verificación de chofer", "configura un método de pago"...). El rechazo
      // por permiso siempre lo redacta el servidor; lo demás (red, errores
      // técnicos en inglés) pasa por mensajeDeError.
      const msg =
        String(e?.code ?? '').includes('permission-denied') && typeof e?.message === 'string' && e.message.trim().length > 0
          ? e.message
          : mensajeDeError(e, 'No pudimos tomar este pedido. Vuelve a intentarlo en un momento.');
      setErrorTomar(msg);
      Alert.alert('No pudimos tomar el pedido', msg);
    } finally {
      setClaimingId(null);
    }
  };

  if (userRole !== 'DELIVERY_DRIVER' && userRole !== 'DRIVER') {
    return (
      <SafeAreaView style={[styles.safeArea, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
        <Ionicons name="lock-closed" size={48} color={Colors.border} />
        <Text style={{ marginTop: 10, color: Colors.text, fontWeight: '700' }}>
          Acceso restringido
        </Text>
        <Text style={{ color: Colors.textSecondary, textAlign: 'center', marginTop: 6 }}>
          Solo repartidores pueden tomar pedidos.
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => volver(router)} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Pedidos disponibles</Text>
        <View style={{ width: 24 }} />
      </View>

      {errorTomar !== null && (
        <View style={styles.errorBanner}>
          <Ionicons name="alert-circle" size={18} color="#B3261E" />
          <Text style={styles.errorText}>{errorTomar}</Text>
        </View>
      )}

      {/* LA PUERTA DE VUELTA A SU PROPIA ENTREGA. Va arriba de todo y antes del
          estado de carga a propósito: si tiene comida encima, es lo primero que
          necesita ver, aunque la lista de pedidos disponibles todavía no haya
          cargado o esté vacía. */}
      {entregaEnCurso !== null && (
        <TouchableOpacity
          onPress={() => router.push(`/delivery-active?orderId=${entregaEnCurso.id}` as any)}
          accessibilityRole="button"
          accessibilityLabel="Continuar la entrega en curso"
          style={styles.entregaEnCurso}
        >
          <Ionicons name="bicycle" size={20} color="#FFF" />
          <View style={{ flex: 1 }}>
            <Text style={styles.entregaEnCursoTitulo}>Tienes una entrega en curso</Text>
            <Text style={styles.entregaEnCursoTexto} numberOfLines={1}>
              {entregaEnCurso.status === 'PICKED_UP'
                ? 'Ya recogiste el pedido — toca para entregarlo'
                : `Recoge en ${entregaEnCurso.restaurantName ?? 'el restaurante'}`}
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={20} color="#FFF" />
        </TouchableOpacity>
      )}

      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={Colors.primary} />
        </View>
      ) : sorted.length === 0 ? (
        <View style={styles.centered}>
          <Ionicons name="bicycle-outline" size={64} color={Colors.border} />
          <Text style={styles.emptyTitle}>Sin pedidos por ahora</Text>
          <Text style={styles.muted}>Te llegará un push cuando un restaurante esté listo.</Text>
        </View>
      ) : (
        <FlatList
          data={sorted}
          keyExtractor={(item) => item.id}
          contentContainerStyle={{ padding: SIZES.padding }}
          renderItem={({ item }) => (
            <View style={styles.card}>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={styles.restName} numberOfLines={1}>{item.restaurantName ?? 'Restaurante'}</Text>
                  {item.distanceKm !== null && (
                    <View style={styles.distanceBadge}>
                      <Ionicons name="navigate" size={12} color={Colors.primary} />
                      <Text style={styles.distanceText}>{item.distanceKm.toFixed(1)} km</Text>
                    </View>
                  )}
                </View>
                {/* V21 — la dirección exacta del cliente NO se enseña aquí. Este
                    feed lo ve cualquier repartidor verificado del país sin haber
                    tomado nada, y la casa de quien pidió comida no es información
                    pública. Lo que hace falta para decidir sí está: el sector y
                    cuánto hay que manejar desde el restaurante. La dirección
                    aparece al tomar el pedido (delivery-active). */}
                <Text style={styles.address} numberOfLines={2}>
                  📍 Entrega: {item.zonaEntrega ?? 'Zona por confirmar'}
                  {typeof item.distanciaEntregaKm === 'number'
                    ? `  ·  ${item.distanciaEntregaKm.toFixed(1)} km desde el restaurante`
                    : ''}
                </Text>
                <View style={styles.metaRow}>
                  <Text style={styles.metaText}>Total: {pesos(item.total ?? 0)}</Text>
                  {item.tip > 0 && (
                    <Text style={[styles.metaText, { color: '#00A86B', fontWeight: '700' }]}>
                      + {pesos(item.tip)} propina
                    </Text>
                  )}
                  <Text style={styles.metaText}>
                    {/* El código interno ('CASH', 'TRANSFER', 'CARD') se leía en inglés. */}
                    {item.paymentMethod === 'CASH'
                      ? 'Efectivo'
                      : item.paymentMethod === 'TRANSFER'
                        ? 'Transferencia'
                        : item.paymentMethod === 'CARD'
                          ? 'Tarjeta'
                          : ''}
                  </Text>
                </View>
              </View>
              <TouchableOpacity
                style={[styles.claimBtn, claimingId === item.id && { opacity: 0.6 }]}
                onPress={() => handleClaim(item.id)}
                disabled={claimingId !== null}
              >
                {claimingId === item.id ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text style={styles.claimBtnText}>Tomar</Text>
                )}
              </TouchableOpacity>
            </View>
          )}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F8F9FA' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#FFF',
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  backBtn: { padding: 5 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: Colors.text },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 30 },
  emptyTitle: { fontSize: 17, fontWeight: '700', color: Colors.text, marginTop: 12 },
  muted: { color: Colors.textSecondary, textAlign: 'center', marginTop: 6 },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF',
    borderRadius: SIZES.radius,
    padding: 14,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  restName: { fontSize: 15, fontWeight: '700', color: Colors.text, flex: 1 },
  distanceBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EAF4FF',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    marginLeft: 8,
  },
  distanceText: { color: Colors.primary, fontSize: 11, marginLeft: 4, fontWeight: '700' },
  address: { fontSize: 13, color: Colors.textSecondary, marginTop: 6 },
  metaRow: { flexDirection: 'row', gap: 10, marginTop: 8, flexWrap: 'wrap' },
  metaText: { fontSize: 12, color: Colors.textSecondary },
  claimBtn: {
    backgroundColor: Colors.primary,
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 10,
    marginLeft: 12,
    minWidth: 70,
    alignItems: 'center',
  },
  claimBtnText: { color: '#FFF', fontWeight: '700', fontSize: 14 },
  entregaEnCurso: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Colors.primary,
    marginHorizontal: SIZES.padding,
    marginTop: 10,
    padding: 14,
    borderRadius: 12,
  },
  entregaEnCursoTitulo: { color: '#FFF', fontWeight: 'bold', fontSize: 15 },
  entregaEnCursoTexto: { color: '#FFF', opacity: 0.9, fontSize: 13, marginTop: 2 },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FDECEA',
    borderWidth: 1,
    borderColor: '#B3261E',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginHorizontal: SIZES.padding,
    marginTop: 12,
  },
  errorText: { flex: 1, color: '#B3261E', fontSize: 13, fontWeight: '600' },
});
